import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you. We'll respond as soon as we can.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(
        err?.response?.data?.detail?.[0]?.msg ||
          "Could not send message right now. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-20 lg:py-28 bg-[#F8FAFC]"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <span className="text-xs uppercase tracking-[0.22em] font-semibold text-[#D97706]">
              Contact
            </span>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-slate-900 leading-tight">
              Reach out. We&apos;d love to hear from you.
            </h2>
            <p className="mt-5 text-slate-600 text-base leading-relaxed">
              For general questions, partnership ideas, or pastoral conversation —
              send a note and our team will be in touch.
            </p>

            <div className="mt-8 space-y-4 text-sm">
              <a
                href="mailto:office@balministries.org"
                data-testid="contact-email-link"
                className="flex items-center gap-3 text-slate-700 hover:text-[#0369A1] transition-colors"
              >
                <span className="h-10 w-10 rounded-full bg-white border border-slate-100 flex items-center justify-center text-[#0369A1]">
                  <Mail size={16} />
                </span>
                office@balministries.org
              </a>
              <div className="flex items-center gap-3 text-slate-700">
                <span className="h-10 w-10 rounded-full bg-white border border-slate-100 flex items-center justify-center text-[#0369A1]">
                  <MapPin size={16} />
                </span>
                In partnership with Begin Again Living Ministries
              </div>
            </div>
          </div>

          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="lg:col-span-7 bg-white border border-slate-100 rounded-2xl p-6 sm:p-8 shadow-sm"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input
                required
                placeholder="Your name"
                data-testid="contact-name"
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                className="rounded-lg border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#0369A1] focus:ring-2 focus:ring-[#0369A1]/15 outline-none px-4 py-3 text-sm"
              />
              <input
                required
                type="email"
                placeholder="Email"
                data-testid="contact-email"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                className="rounded-lg border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#0369A1] focus:ring-2 focus:ring-[#0369A1]/15 outline-none px-4 py-3 text-sm"
              />
            </div>
            <textarea
              required
              rows={6}
              placeholder="How can we help?"
              data-testid="contact-message"
              value={form.message}
              onChange={(e) => update("message", e.target.value)}
              className="mt-4 w-full rounded-lg border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#0369A1] focus:ring-2 focus:ring-[#0369A1]/15 outline-none px-4 py-3 text-sm resize-none"
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit"
              className="mt-6 inline-flex items-center gap-2 bg-[#0369A1] hover:bg-[#0284C7] disabled:opacity-60 text-white rounded-full px-6 py-3 text-sm font-medium transition-colors"
            >
              {loading ? "Sending..." : (<><Send size={14} /> Send message</>)}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
